import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import type React from "react";
import { useMemo, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button.tsx";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import SubmitButton from "~/components/formElements/SubmitButton";
import Switch from "~/components/formElements/Switch/Switch";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import {
	DEFAULT_COUNTRY_CODE,
	DEFAULT_LANGUAGE_ID,
	DEFAULT_SALES_FUNNEL_ID,
	DEFAULT_SALES_TYPE_ID,
} from "~/constants/form.ts";
import { useAuth } from "~/contexts/AuthContext";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import useGenderSelectOptions from "~/hooks/form/formOptionsData/useGenderSelectOptions.ts";
import useLanguageSelectOptions from "~/hooks/form/formOptionsData/useLanguageSelectOptions.ts";
import useSalesFunnelSelectOptions from "~/hooks/form/formOptionsData/useSalesFunnelSelectOptions.ts";
import useSalesTypeSelectOptions from "~/hooks/form/formOptionsData/useSalesTypeSelectOptions.ts";
import useUserSelectOptions, { employeesFilterFn } from "~/hooks/form/formOptionsData/useUsersSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import { useAllClients } from "~/modules/client/api/client/clientQueries.ts";
import {
	createClientContactPerson,
} from "~/modules/client/api/clientContactPerson/clientContactPersonApiDispatchers.ts";
import type {
	CreateClientContactPersonData,
} from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import ContactCompanyFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactCompanyFormSection";
import ContactContactFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactContactFormSection";
import ContactPersonalInformationFormSection
	from "~/modules/client/components/contact/forms/formSections/ContactPersonalInformationFormSection";
import ContactSalesFormSection from "~/modules/client/components/contact/forms/formSections/ContactSalesFormSection";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import {
	mapDataToInputOptionsData,
	preventSubmitOnEnter,
	sanitizeStringForComparison,
	transformEmptyDateValueToNull,
} from "~/utils/form/formUtils.ts";

type CreateClientContactPersonFormProps = {
	onSuccess: (clientId: string) => void;
	onCancel: () => void;
	clientId?: string;
	companyId: string;
	disableClientSelect?: boolean;
};

interface CreateClientContactPersonFormData extends Omit<CreateClientContactPersonData, "dateOfBirth" | "address"> {
	addressLine1: string | null;
	addressLine2: string | null;
	addressLine3: string | null;
	city: string | null;
	postCode: string | null;
	countryCode: string | null;
	dateOfBirth: Date | null;
}

const userFilterOptions = { filterFunction: employeesFilterFn };

const CreateClientContactPersonForm: React.FC<CreateClientContactPersonFormProps> = ({
	onSuccess,
	onCancel,
	companyId,
	clientId,
}) => {
	const [busy, setBusy] = useState(false);
	const { user } = useAuth();
	const { data: allClientsData } = useAllClients();

	const schema = useMemo(() => {
		return yup.object({
			addressLine1: yup.string().default(null),
			addressLine2: yup.string().default(null),
			addressLine3: yup.string().default(null),
			city: yup.string().default(null),
			clientId: yup.string().required(),
			countryCode: yup.string().default(null),
			dateOfBirth: yup.date().transform(transformEmptyDateValueToNull).nullable().default(null),
			emailBusiness: yup.string().email().required().default(null),
			emailPrivate: yup.string().email().default(null),
			firstName: yup.string().required(),
			genderId: yup.string().required(),
			hasDeviatingAddress: yup.boolean().required(),
			jobTitle: yup.string().default(null),
			languageId: yup.string().required(),
			lastName: yup.string().required(),
			managedBy: yup.string().required(),
			mobilePhone: yup.string().default(null),
			nobilityTitle: yup.string().default(null),
			phone: yup.string().default(null),
			postCode: yup.string().default(null),
			salesFunnelId: yup.string().required(),
			salesTypeId: yup.string().required(),
			title: yup.string().default(null),
			useInformalSalutation: yup.boolean().required(),
		});
	}, []);

	const defaultValues = useMemo(() => {
		return {
			addressLine1: "",
			addressLine2: "",
			addressLine3: "",
			city: "",
			clientId: clientId || "",
			countryCode: DEFAULT_COUNTRY_CODE,
			dateOfBirth: null,
			emailBusiness: "",
			emailPrivate: "",
			firstName: "",
			genderId: "",
			hasDeviatingAddress: false,
			jobTitle: "",
			languageId: DEFAULT_LANGUAGE_ID,
			lastName: "",
			managedBy: user?.id || "",
			mobilePhone: "",
			nobilityTitle: "",
			phone: "",
			postCode: "",
			salesFunnelId: DEFAULT_SALES_FUNNEL_ID,
			salesTypeId: DEFAULT_SALES_TYPE_ID,
			title: "",
			useInformalSalutation: false,
		};
	}, [user, clientId]);

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty, isValid, isSubmitted },
		watch,
		setError,
	} = useForm<CreateClientContactPersonFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<CreateClientContactPersonFormData>(schema),
	});
	const formIsSubmittable = useFormIsSubmittable({ isDirty, isSubmitted, isValid, isLoading: busy });
	const countryCodeSelectOptions = useCountryCodeSelectOptions();
	const genderSelectOptions = useGenderSelectOptions();
	const languageSelectOptions = useLanguageSelectOptions();
	const managerSelectOptions = useUserSelectOptions(userFilterOptions);
	const salesFunnelSelectOptions = useSalesFunnelSelectOptions();
	const salesTypeSelectOptions = useSalesTypeSelectOptions();

	const clientSelectOptions = useMemo(() => {
		if (allClientsData) {
			const companiesClients = allClientsData.filter((client) => client.companyId === companyId);
			return mapDataToInputOptionsData(companiesClients, "id", "displayName");
		}
		return [];
	}, [allClientsData, companyId]);

	const selectedClientId = watch("clientId");
	const hasDeviatingAddress = watch("hasDeviatingAddress");

	const selectedClient = useMemo(() => {
		return allClientsData?.find((client) => client.id === selectedClientId);
	}, [allClientsData, selectedClientId]);

	const addressMatchesClient = (data: CreateClientContactPersonFormData): boolean => {
		const clientAddress = selectedClient?.address;
		if (!clientAddress) return false;

		const normalize = (val: string | null | undefined) => sanitizeStringForComparison(val || "");

		return normalize(data.addressLine1) === normalize(clientAddress.addressLine1) &&
			normalize(data.addressLine2) === normalize(clientAddress.addressLine2) &&
			normalize(data.addressLine3) === normalize(clientAddress.addressLine3) &&
			normalize(data.city) === normalize(clientAddress.city) &&
			normalize(data.postCode) === normalize(clientAddress.postCode) &&
			normalize(data.countryCode) === normalize(clientAddress.countryCode);
	};

	const onSubmit: SubmitHandler<CreateClientContactPersonFormData> = async (data) => {
		if (data.hasDeviatingAddress && addressMatchesClient(data)) {
			setError("addressLine1", { message: "Die Adresse stimmt mit der Firmenadresse überein." });
			return;
		}

		setBusy(true);
		const dateOfBirth = data.dateOfBirth ? formatDateToYYYYMMDD(new Date(data.dateOfBirth)) : null;
		const { addressLine1, addressLine2, addressLine3, city, postCode, countryCode, ...rest } = data;

		const contactData: CreateClientContactPersonData = {
			...rest,
			dateOfBirth,
		};

		if (data.hasDeviatingAddress) {
			contactData.address = { addressLine1, addressLine2, addressLine3, city, postCode, countryCode };
		}

		try {
			await createClientContactPerson(contactData.clientId, contactData);
			onSuccess(data.clientId);
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Neuen Kontakt anlegen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<ContactCompanyFormSection<CreateClientContactPersonFormData>
						control={control}
						disableClientSelect={!!clientId}
						clientSelectOptions={clientSelectOptions}
						errors={errors}
					/>

					<ContactPersonalInformationFormSection<CreateClientContactPersonFormData>
						control={control}
						errors={errors}
						genderSelectOptions={genderSelectOptions}
					/>

					<ContactContactFormSection<CreateClientContactPersonFormData>
						control={control} />

					<FormSection title="Adresse">
						<Switch
							control={control}
							name="hasDeviatingAddress"
							label="Abweichende Adresse"
						/>
					</FormSection>

					{hasDeviatingAddress && (
						<AddressFormSection<CreateClientContactPersonFormData>
							control={control}
							countryCodeSelectOptions={countryCodeSelectOptions}
						/>
					)}

					<ContactSalesFormSection
						control={control}
						errors={errors}
						languageSelectOptions={languageSelectOptions}
						managerSelectOptions={managerSelectOptions}
						salesFunnelSelectOptions={salesFunnelSelectOptions}
						salesTypeSelectOptions={salesTypeSelectOptions}
					/>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint
					show={isSubmitted && !isValid}
					className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancel}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default CreateClientContactPersonForm;
